import { BrowserMultiFormatOneDReader, BrowserCodeReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { mountAnimatedSheet, closeAnimatedSheet } from "../utils/sheetAnimation";

interface ScannerControls {
    stop: () => void;
}

export function openScannerOverlay(): Promise<string | null> {
    return new Promise((resolve) => {
        let controls: ScannerControls | null = null;
        let settled = false;
        let cancelled = false;
        let openTimeout = -1;
        let onOpen: (e: TransitionEvent) => void = () => {};
        let detectorLoop: number | null = null;
        let detecting = false;

        const overlay = document.createElement("div");
        overlay.className = "sheet-overlay";

        const sheet = document.createElement("div");
        sheet.className = "item-sheet";

        const header = document.createElement("header");
        header.className = "item-sheet-header";

        const title = document.createElement("h2");
        title.className = "item-sheet-title";
        title.textContent = "Scan barcode";

        const dismissBtn = document.createElement("button");
        dismissBtn.type = "button";
        dismissBtn.className = "item-sheet-dismiss";
        dismissBtn.setAttribute("aria-label", "Cancel");
        dismissBtn.textContent = "×";
        header.appendChild(title);
        header.appendChild(dismissBtn);

        const body = document.createElement("div");
        body.className = "item-sheet-body";

        const card = document.createElement("div");
        card.className = "scan-viewfinder-card";

        const viewfinder = document.createElement("div");
        viewfinder.className = "scan-viewfinder";
        viewfinder.id = "scanner-container";

        const video = document.createElement("video");
        video.setAttribute("playsinline", "true");
        video.setAttribute("muted", "true");
        video.setAttribute("autoplay", "true");

        const viewfinderOverlay = document.createElement("div");
        viewfinderOverlay.className = "scan-viewfinder-overlay";

        const frame = document.createElement("div");
        frame.className = "scan-viewfinder-frame";

        viewfinderOverlay.appendChild(frame);
        viewfinder.appendChild(video);
        viewfinder.appendChild(viewfinderOverlay);
        card.appendChild(viewfinder);
        body.appendChild(card);

        const hint = document.createElement("p");
        hint.className = "scanner-overlay-hint";
        hint.textContent = "Point your camera at a barcode";

        body.appendChild(hint);

        sheet.appendChild(header);
        sheet.appendChild(body);
        overlay.appendChild(sheet);

        function finish(code: string | null): void {
            if (settled) return;
            settled = true;
            cancelled = true;
            window.clearTimeout(openTimeout);
            if (detectorLoop) window.clearTimeout(detectorLoop);
            sheet.removeEventListener("transitionend", onOpen);
            controls?.stop();
            BrowserCodeReader.releaseAllStreams();
            closeAnimatedSheet(overlay, () => resolve(code));
        }

        dismissBtn.addEventListener("click", () => finish(null));
        overlay.addEventListener("click", (e) => {
            if (e.target === overlay) finish(null);
        });

        mountAnimatedSheet(overlay);

        const hints = new Map<DecodeHintType, any>();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
            BarcodeFormat.EAN_13,
            BarcodeFormat.EAN_8,
            BarcodeFormat.UPC_A,
            BarcodeFormat.UPC_E,
            BarcodeFormat.CODE_128,
            BarcodeFormat.CODE_39,
            BarcodeFormat.CODE_93,
            BarcodeFormat.ITF,
        ]);

        const reader = new BrowserMultiFormatOneDReader(hints, {
            delayBetweenScanAttempts: 100,
            delayBetweenScanSuccess: 1000,
            tryPlayVideoTimeout: 5000,
        });

        const preferredConstraints = {
            video: {
                facingMode: { ideal: "environment" as const },
                width: { ideal: 1280 },
                height: { ideal: 720 },
            },
        };
        let triedFallback = false;

        function startWithConstraints(constraints: MediaStreamConstraints): void {
            if (cancelled) return;
            reader.decodeFromConstraints(constraints, video, (result, error) => {
                if (error || !result?.getText()) {
                    return;
                }
                finish(result.getText());
            })
                .then((ctrl) => {
                    controls = ctrl;
                    hint.textContent = "Scanning…";
                    startBarcodeDetector(ctrl);
                })
                .catch(() => {
                    if (!triedFallback) {
                        triedFallback = true;
                        startWithConstraints({ video: true });
                    } else {
                        finish(null);
                    }
                });
        }

        async function startBarcodeDetector(_ctrl: ScannerControls): Promise<void> {
            const BarcodeDetectorClass = (globalThis as any).BarcodeDetector;
            if (!BarcodeDetectorClass) {
                return;
            }

            const desiredFormats = [
                "ean_13",
                "ean_8",
                "upc_a",
                "upc_e",
                "code_128",
                "code_39",
                "itf",
                "qr_code",
            ];
            let formats = desiredFormats;
            if (typeof BarcodeDetectorClass.getSupportedFormats === "function") {
                try {
                    const supported = await BarcodeDetectorClass.getSupportedFormats();
                    formats = desiredFormats.filter((f) => supported.includes(f));
                } catch {
                    // fall back to the full desired list
                }
            }

            let detector: any;
            try {
                detector = new BarcodeDetectorClass({ formats });
            } catch {
                return;
            }

            function next(): void {
                if (settled || cancelled) return;
                if (detecting || !video.videoWidth || video.paused) {
                    detectorLoop = window.setTimeout(next, 80);
                    return;
                }
                detecting = true;
                detector
                    .detect(video)
                    .then((barcodes: Array<{ rawValue?: string }>) => {
                        if (barcodes?.length && barcodes[0].rawValue) {
                            finish(barcodes[0].rawValue);
                            return;
                        }
                    })
                    .catch(() => {
                        // ignore frame-level detection errors
                    })
                    .finally(() => {
                        detecting = false;
                        if (!settled && !cancelled) {
                            detectorLoop = window.setTimeout(next, 80);
                        }
                    });
            }
            next();
        }

        let started = false;
        const startScan = () => {
            if (started || cancelled) return;
            started = true;
            startWithConstraints(preferredConstraints);
        };

        onOpen = (e: TransitionEvent) => {
            if (e.target === sheet && e.propertyName === "transform") {
                sheet.removeEventListener("transitionend", onOpen);
                window.clearTimeout(openTimeout);
                startScan();
            }
        };
        sheet.addEventListener("transitionend", onOpen);
        openTimeout = window.setTimeout(() => {
            sheet.removeEventListener("transitionend", onOpen);
            startScan();
        }, 500);
    });
}
